import React, { useState, useRef, useCallback } from 'react';

const YouTubeEmbed = ({ 
  videoId,
  playlistId,
  title = 'Video Player',
  width = 300,
  height = 300,
  onClose 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef(null);
  
  const getEmbedUrl = useCallback(() => {
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      rel: '0',
      modestbranding: '1',
      enablejsapi: '1',
      playsinline: '1'
    });
    
    if (playlistId) {
      params.set('list', playlistId);
      params.set('listType', 'playlist');
      return `https://www.youtube.com/embed/videoseries?${params.toString()}`;
    } else if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    }
    return null;
  }, [videoId, playlistId]);

  const toggleMute = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      const command = isMuted ? 'unMute' : 'mute';
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command, args: [] }),
        '*'
      );
      
      if (isMuted) {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'setVolume', args: [100] }),
          '*'
        );
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const embedUrl = getEmbedUrl();
  if (!embedUrl) return null;

  const headerHeight = 44;
  const videoHeight = isExpanded ? '100%' : height - headerHeight;
  
  const styles = {
    container: {
      width: isExpanded ? '100%' : width,
      height: isExpanded ? '100%' : height,
      backgroundColor: '#000',
      borderRadius: 8,
      overflow: 'hidden',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: isExpanded ? 'fixed' : 'relative',
      top: isExpanded ? 0 : 'auto',
      left: isExpanded ? 0 : 'auto',
      right: isExpanded ? 0 : 'auto',
      bottom: isExpanded ? 0 : 'auto',
      zIndex: isExpanded ? 9999 : 'auto',
      boxShadow: isExpanded ? 'none' : '0 8px 32px rgba(0,0,0,0.4)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 12px',
      background: 'linear-gradient(to right, #dc2626, #b91c1c)',
      height: headerHeight,
      boxSizing: 'border-box',
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 8 },
    title: { color: '#fff', fontSize: 13, fontWeight: 600, margin: 0 },
    headerRight: { display: 'flex', alignItems: 'center', gap: 4 },
    button: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      borderRadius: 6,
      padding: 6,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      transition: 'background 0.2s',
    },
    buttonActive: { background: '#22c55e' },
    videoContainer: {
      position: 'relative',
      height: isExpanded ? `calc(100% - ${headerHeight}px)` : videoHeight,
      backgroundColor: '#000',
    },
    iframe: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' },
    soundButton: {
      position: 'absolute',
      bottom: 10,
      left: 10,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 12px',
      background: 'rgba(0,0,0,0.8)',
      border: 'none',
      borderRadius: 6,
      color: '#fff',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      zIndex: 10,
    },
    backdrop: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      zIndex: -1,
    },
  };

  const YouTubeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );

  const VolumeOffIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
    </svg>
  );

  const VolumeOnIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
    </svg>
  );

  const ExpandIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
      <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
  );

  const MinimizeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
      <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
  );

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <YouTubeIcon />
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.headerRight}>
          <button onClick={toggleMute} style={{ ...styles.button, ...(isMuted ? {} : styles.buttonActive) }} title={isMuted ? 'Turn sound on' : 'Mute'}>
            {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
          </button>
          <button onClick={() => setIsExpanded(!isExpanded)} style={styles.button} title={isExpanded ? 'Minimize' : 'Expand'}>
            {isExpanded ? <MinimizeIcon /> : <ExpandIcon />}
          </button>
          {onClose && <button onClick={onClose} style={styles.button} title="Close"><CloseIcon /></button>}
        </div>
      </div>
      
      <div style={styles.videoContainer}>
        <iframe 
          ref={iframeRef} 
          src={embedUrl} 
          style={styles.iframe} 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowFullScreen 
          title={title} 
        />
        {isMuted && (
          <button onClick={toggleMute} style={styles.soundButton}>
            <VolumeOnIcon /> Sound On
          </button>
        )}
      </div>
      
      {isExpanded && <div style={styles.backdrop} onClick={() => setIsExpanded(false)} />}
    </div>
  );
};

export default YouTubeEmbed;